import { PERMISSIONS } from '@/constants/permissions';
import { UserAuth } from '@/models/auth/UserAuth';
import { useAuthContext } from '@/providers/AuthProvider';
import { useProfileStore } from '@/stores/profileStore';
import { checkGuardAllowAccess } from '@/utils/permission';

// Authentication

export const checkRevokeToken = (auth: UserAuth | undefined) =>
  checkGuardAllowAccess(auth, [
    { actions: [PERMISSIONS.auth.actions.revokeToken.code] },
  ]);

export const useAuthPermission = () => {
  const { valAuth: auth } = useProfileStore();
  return {
    allowRevokeToken: checkRevokeToken(auth),
  };
};

// Client

export const checkViewClient = (auth: UserAuth | undefined) =>
  checkGuardAllowAccess(auth, [
    { actions: [PERMISSIONS.client.actions.read.code] },
  ]);

export const checkCreateClient = (auth: UserAuth | undefined) =>
  checkGuardAllowAccess(auth, [
    { actions: [PERMISSIONS.client.actions.create.code] },
  ]);

export const checkUpdateClient = (auth: UserAuth | undefined) =>
  checkGuardAllowAccess(auth, [
    { actions: [PERMISSIONS.client.actions.update.code] },
  ]);

export const checkDeleteClient = (auth: UserAuth | undefined) =>
  checkGuardAllowAccess(auth, [
    { actions: [PERMISSIONS.client.actions.delete.code] },
  ]);

export const checkArchiveClient = (auth: UserAuth | undefined) =>
  checkGuardAllowAccess(auth, [
    { actions: [PERMISSIONS.client.actions.archive.code] },
  ]);

export const checkUnarchiveClient = (auth: UserAuth | undefined) =>
  checkGuardAllowAccess(auth, [
    { actions: [PERMISSIONS.client.actions.unarchive.code] },
  ]);

export const useClientPermission = () => {
  const { valAuth: auth } = useProfileStore();
  return {
    allowViewClient: checkViewClient(auth),
    allowCreateClient: checkCreateClient(auth),
    allowUpdateClient: checkUpdateClient(auth),
    allowDeleteClient: checkDeleteClient(auth),
    allowArchiveClient: checkArchiveClient(auth),
    allowUnarchiveClient: checkUnarchiveClient(auth),
  };
};

// Operator

export const checkViewOperator = (auth: UserAuth | undefined) =>
  checkGuardAllowAccess(auth, [
    { actions: [PERMISSIONS.operator.actions.read.code] },
  ]);

export const checkCreateOperator = (auth: UserAuth | undefined) =>
  checkGuardAllowAccess(auth, [
    { actions: [PERMISSIONS.operator.actions.create.code] },
  ]);

export const checkUpdateOperator = (auth: UserAuth | undefined) =>
  checkGuardAllowAccess(auth, [
    { actions: [PERMISSIONS.operator.actions.update.code] },
  ]);

export const checkDeleteOperator = (auth: UserAuth | undefined) =>
  checkGuardAllowAccess(auth, [
    { actions: [PERMISSIONS.operator.actions.delete.code] },
  ]);

export const checkArchiveOperator = (auth: UserAuth | undefined) =>
  checkGuardAllowAccess(auth, [
    { actions: [PERMISSIONS.operator.actions.archive.code] },
  ]);

export const checkUnarchiveOperator = (auth: UserAuth | undefined) =>
  checkGuardAllowAccess(auth, [
    { actions: [PERMISSIONS.operator.actions.unarchive.code] },
  ]);

export const checkAssignRoleForOperator = (auth: UserAuth | undefined) =>
  checkGuardAllowAccess(auth, [
    { actions: [PERMISSIONS.operator.actions.assignRoles.code] },
  ]);

export const checkUnassignRoleForOperator = (auth: UserAuth | undefined) =>
  checkGuardAllowAccess(auth, [
    { actions: [PERMISSIONS.operator.actions.unassignRoles.code] },
  ]);

export const useOperatorPermission = () => {
  const { valAuth: auth } = useProfileStore();
  return {
    allowViewOperator: checkViewOperator(auth),
    allowCreateOperator: checkCreateOperator(auth),
    allowUpdateOperator: checkUpdateOperator(auth),
    allowDeleteOperator: checkDeleteOperator(auth),
    allowArchiveOperator: checkArchiveOperator(auth),
    allowUnarchiveOperator: checkUnarchiveOperator(auth),
    allowAssignRoleForOperator: checkAssignRoleForOperator(auth),
    allowUnassignRoleForOperator: checkUnassignRoleForOperator(auth),
  };
};

// Role

export const checkViewRole = (auth: UserAuth | undefined) =>
  checkGuardAllowAccess(auth, [
    { actions: [PERMISSIONS.role.actions.read.code] },
  ]);

export const checkCreateRole = (auth: UserAuth | undefined) =>
  checkGuardAllowAccess(auth, [
    { actions: [PERMISSIONS.role.actions.create.code] },
  ]);

export const checkUpdateRole = (auth: UserAuth | undefined) =>
  checkGuardAllowAccess(auth, [
    { actions: [PERMISSIONS.role.actions.update.code] },
  ]);

export const checkDeleteRole = (auth: UserAuth | undefined) =>
  checkGuardAllowAccess(auth, [
    { actions: [PERMISSIONS.role.actions.delete.code] },
  ]);

export const checkAssignPermissionsForRole = (auth: UserAuth | undefined) =>
  checkGuardAllowAccess(auth, [
    { actions: [PERMISSIONS.role.actions.assignPermissions.code] },
  ]);

export const checkUnassignPermissionsForRole = (auth: UserAuth | undefined) =>
  checkGuardAllowAccess(auth, [
    { actions: [PERMISSIONS.role.actions.unassignPermissions.code] },
  ]);

export const useRolePermission = () => {
  const { valAuth: auth } = useProfileStore();
  return {
    allowViewRole: checkViewRole(auth),
    allowCreateRole: checkCreateRole(auth),
    allowUpdateRole: checkUpdateRole(auth),
    allowDeleteRole: checkDeleteRole(auth),
    allowAssignPermissionsForRole: checkAssignPermissionsForRole(auth),
    allowUnassignPermissionsForRole: checkUnassignPermissionsForRole(auth),
  };
};

// Report

export const checkViewDashboard = (auth: UserAuth | undefined) =>
  checkGuardAllowAccess(auth, [
    { actions: [PERMISSIONS.report.actions.viewDashboard.code] },
  ]);

export const useReportPermission = () => {
  const { valAuth: auth } = useProfileStore();
  return {
    allowViewDashboard: checkViewDashboard(auth),
  };
};
